import React, {useState} from 'react';
import {DEFAULT_GRADIENT, DEFAULT_GRADIENT_HOVER, DEFAULT_THEME_COLOR} from "../../constants/theme";
import Button from "@mui/material/Button";
import Fab from "@mui/material/Fab";
import useTranslation from "../../customHooks/translations";
import {Global} from "@emotion/react";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import {styled} from "@mui/material/styles";
import {IconButton} from "@mui/material";
import GifIcon from "@mui/icons-material/Gif";
import MessageIcon from '@mui/icons-material/Message';
import CloseIcon from '@mui/icons-material/Close';
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import agent from "../../agent";
import GiphyComponent from "./GiphyComponent";
import {ADD_COMMENT} from "../../constants/actionTypes";
import {connect} from "react-redux";
import {Editor} from "react-draft-wysiwyg";
import {EditorState, convertToRaw} from 'draft-js';
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import draftToHtml from 'draftjs-to-html';
import "./style.css";
import {getLanguageByBrowser} from "../../utils/languageUtils";

const Puller = styled('div')({
  width: 36,
  height: 4,
  borderRadius: 2,
  backgroundColor: 'rgba(255,255,255,0.2)',
  position: 'absolute',
  top: 10,
  left: 'calc(50% - 18px)',
});

const PostCommentComponent = (props) => {
  const translation = useTranslation();
  const [open, setOpen] = useState(false);
  const [gifsVisible, setGifsVisible] = useState(false);
  const [editorState, setEditorState] = useState(EditorState.createEmpty());

  const createComment = (ev, gifId = null) => {
    ev?.preventDefault();
    const raw = convertToRaw(editorState.getCurrentContent());
    const html = draftToHtml(raw);
    const payload = agent.Comments.createForFreak(props.slug, { body: html, gifId, replyTo: props.replyTo });
    props.onSubmit(payload);
    setEditorState(EditorState.createEmpty());
    setOpen(false);
  };

  const triggerButton = props.fab
    ? (
      <Fab
        size="medium"
        onClick={() => setOpen(true)}
        sx={{
          background: DEFAULT_GRADIENT,
          color: 'white',
          boxShadow: '0 4px 20px rgba(54,201,162,0.4)',
          '&:hover': { background: DEFAULT_GRADIENT_HOVER },
          '&:focus': { outline: 'none' },
        }}
      >
        <MessageIcon />
      </Fab>
    )
    : props.compact
    ? (
      <IconButton
        size="small"
        onClick={() => setOpen(true)}
        sx={{ color: 'rgba(255,255,255,0.35)', '&:hover': { color: DEFAULT_THEME_COLOR }, '&:focus': { outline: 'none' }, p: 0.5 }}
      >
        <MessageIcon fontSize="small" />
      </IconButton>
    )
    : (
      <Button
        variant="contained"
        fullWidth={!!props.fullWidth}
        size={props.fullWidth ? 'large' : 'small'}
        onClick={() => setOpen(true)}
        startIcon={<MessageIcon />}
        sx={{
          background: DEFAULT_GRADIENT,
          color: 'white',
          fontWeight: 700,
          zIndex: 0,
          borderRadius: props.fullWidth ? 3 : 2,
          py: props.fullWidth ? 1.5 : undefined,
          boxShadow: props.fullWidth ? '0 4px 24px rgba(54,201,162,0.3)' : undefined,
          '&:hover': { background: DEFAULT_GRADIENT_HOVER },
          '&:focus': { outline: 'none' },
        }}
      >
        {props.text}
      </Button>
    );

  return (
    <>
      {triggerButton}

      <Global
        styles={{
          '.MuiDrawer-root > .MuiPaper-root': {
            height: '85vh',
            maxHeight: '85vh',
            overflow: 'hidden',
          },
        }}
      />

      <SwipeableDrawer
        anchor="bottom"
        open={open}
        onClose={() => setOpen(false)}
        onOpen={() => setOpen(true)}
        swipeAreaWidth={0}
        disableSwipeToOpen={false}
        ModalProps={{ keepMounted: true }}
      >
        <Box
          sx={{
            position: 'relative',
            height: '100%',
            background: 'linear-gradient(180deg, #0d1b2a 0%, #091420 100%)',
            pt: '28px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Puller />

          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              pb: 1.5,
              borderBottom: '1px solid rgba(255,255,255,0.07)',
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                background: DEFAULT_GRADIENT,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                fontWeight: 700,
              }}
            >
              {translation.comments.postComment}
            </Typography>
            <IconButton
              size="small"
              onClick={() => setOpen(false)}
              sx={{ color: 'rgba(255,255,255,0.4)', '&:hover': { color: 'white' }, '&:focus': { outline: 'none' } }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Editor + buttons in one scrollable area */}
          <Box sx={{ flex: 1, px: 2, py: 1.5, overflow: 'auto' }}>
            <Editor
              editorState={editorState}
              toolbarClassName="toolbarClassName"
              wrapperClassName="wrapperClassName"
              editorClassName="editorClassName editor-border"
              onEditorStateChange={setEditorState}
              toolbar={{ options: ['link', 'emoji'] }}
              localization={{ locale: getLanguageByBrowser() }}
            />

            {/* Buttons directly below editor */}
            <Box
              sx={{
                mt: 2,
                pt: 1.5,
                borderTop: '1px solid rgba(255,255,255,0.07)',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <IconButton
                aria-label="gif"
                onClick={() => setGifsVisible(v => !v)}
                sx={{
                  background: gifsVisible ? DEFAULT_GRADIENT : 'rgba(255,255,255,0.08)',
                  color: 'white',
                  width: 36,
                  height: 36,
                  borderRadius: 2,
                  '&:hover': { background: DEFAULT_GRADIENT },
                  '&:focus': { outline: 'none' },
                }}
              >
                <GifIcon />
              </IconButton>

              <Box sx={{ flex: 1 }} />

              <Button
                variant="outlined"
                size="small"
                onClick={() => setOpen(false)}
                sx={{ color: 'rgba(255,255,255,0.55)', borderColor: 'rgba(255,255,255,0.2)', zIndex: 0, borderRadius: 2, '&:hover': { borderColor: 'rgba(255,255,255,0.5)' }, '&:focus': { outline: 'none' } }}
              >
                {translation.comments.cancel}
              </Button>

              <Button
                variant="contained"
                size="small"
                onClick={() => createComment()}
                sx={{ background: DEFAULT_GRADIENT, color: 'white', fontWeight: 700, zIndex: 0, borderRadius: 2, px: 3, '&:hover': { background: DEFAULT_GRADIENT_HOVER }, '&:focus': { outline: 'none' } }}
              >
                {translation.comments.add}
              </Button>
            </Box>

            {gifsVisible && (
              <Box sx={{ mt: 2 }}>
                <GiphyComponent
                  createComment={(ev, gif) => createComment(ev, gif.id)}
                  setGifsVisible={setGifsVisible}
                />
              </Box>
            )}
          </Box>
        </Box>
      </SwipeableDrawer>
    </>
  );
};

const mapStateToProps = state => ({ ...state });

const mapDispatchToProps = dispatch => ({
  onSubmit: payload => dispatch({ type: ADD_COMMENT, payload })
});

export default connect(mapStateToProps, mapDispatchToProps)(PostCommentComponent);
